import { useCallback, useContext, useMemo } from "react";

import * as LocaleUtils from "../util/LocaleUtils";
import { LocaleContext, translate } from "../util/LocaleUtils";
import { ComposedTranslatable, Translatable } from "../util/Translatable";

export const useTranslated = <
	E extends
		| undefined
		| null
		| LocaleUtils.TextElement
		| Array<LocaleUtils.TextElement>,
>(
	elements: E,
	processor?: (...text: string[]) => string,
): [E extends undefined | null ? undefined | null : string] => {
	const translate = useTranslate();
	return [
		useMemo(
			() => translate(elements, processor),
			[translate, elements, processor],
		) as any,
	];
};

export type TranslateFunc = (
	elements:
		| undefined
		| null
		| LocaleUtils.TextElement
		| Array<LocaleUtils.TextElement>,
	processor?: (...text: string[]) => string,
) => string;

export const useTranslate = (): TranslateFunc => {
	const { locale } = useContext(LocaleUtils.LocaleContext);

	return useCallback<TranslateFunc>(
		(elements, processor) => {
			if (
				typeof elements !== "string" &&
				!Array.isArray(elements) &&
				!(elements instanceof Translatable)
			) {
				return elements;
			}

			return translate(
				new ComposedTranslatable(
					Array.isArray(elements) ? elements : ([elements] as any),
					processor ?? ((text: string) => text),
				),
				locale,
			) as any;
		},
		[locale],
	);
};

export const useLocaleAware = () => {
	const { key } = useContext(LocaleContext);
	return key;
};
